import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { API } from '../../utils/api';
import { trackCheckoutStarted, trackPageView } from '../../utils/analytics';
import axios from 'axios';
import { CheckCircle, ArrowRight, CircleNotch, Star, Gift } from '@phosphor-icons/react';

export default function PricingPage() {
  const { t, language } = useLanguage();
  const { user, isPaid } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Get referral code from URL or localStorage
  const urlReferralCode = searchParams.get('ref');
  const storedReferralCode = typeof window !== 'undefined' ? localStorage.getItem('paperpal_referral_code') : null;
  const referralCode = urlReferralCode || storedReferralCode;
  const hasReferral = !!referralCode;
  
  // Store referral code if from URL
  useEffect(() => {
    if (urlReferralCode) {
      localStorage.setItem('paperpal_referral_code', urlReferralCode);
    }
    // Track page view
    trackPageView('pricing');
  }, [urlReferralCode]);

  // Translations for pricing
  const pricingText = {
    en: {
      referralBanner: "You've been invited — $10 off has been applied",
      earlyAccess: "Early access pricing",
      earlyAccessReferral: "Early access price + $10 referral applied",
      oneTime: "One-time payment — no subscription"
    },
    fr: {
      referralBanner: "Vous avez été invité — 10 $ de rabais appliqué",
      earlyAccess: "Prix d'accès anticipé",
      earlyAccessReferral: "Prix d'accès anticipé + 10 $ de parrainage appliqué",
      oneTime: "Paiement unique — sans abonnement"
    }
  };
  const pt = pricingText[language] || pricingText.en;

  const handleCheckout = async () => {
    if (!user) {
      const signupUrl = referralCode ? `/signup?ref=${referralCode}` : '/signup';
      navigate(signupUrl);
      return;
    }

    if (isPaid) {
      navigate('/dashboard');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Track checkout started
      trackCheckoutStarted(hasReferral ? 39 : 49);
      
      const response = await axios.post(
        `${API}/payments/create-checkout`,
        { referral_code: referralCode || undefined }
      );

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(
        language === 'fr' 
          ? "Erreur lors de la création du paiement. Veuillez réessayer."
          : "Failed to create checkout. Please try again."
      );
      setLoading(false);
    }
  };

  // Already premium - show confirmation
  if (isPaid) {
    return (
      <div className="min-h-screen bg-pp-bg">
        <Header />
        <main className="pt-24 pb-16">
          <div className="pp-container">
            <div className="max-w-2xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 mx-auto mb-6 flex items-center justify-center">
                  <Star size={40} weight="fill" className="text-white" />
                </div>
                <h1 className="pp-heading-1 text-pp-text mb-4">
                  {language === 'fr' ? "Vous êtes Premium !" : "You're Premium!"}
                </h1>
                <p className="text-lg text-pp-text-secondary mb-8">
                  {language === 'fr' 
                    ? "Vous avez accès à toutes les fonctionnalités de PaperPal."
                    : "You have access to all PaperPal features."}
                </p>
                <Link to="/dashboard" className="pp-btn-primary">
                  {language === 'fr' ? "Aller au tableau de bord" : "Go to Dashboard"}
                </Link>
              </motion.div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-pp-bg">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="pp-container">
          <div className="max-w-lg mx-auto">
            
            {/* Referral Banner - only show if referral is active */}
            {hasReferral && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 rounded-xl bg-pp-success-muted border border-pp-success/20 flex items-center justify-center gap-3"
                data-testid="referral-banner"
              >
                <span className="text-xl">🎉</span>
                <span className="text-sm font-medium text-pp-success-text">
                  {pt.referralBanner}
                </span>
              </motion.div>
            )}

            {/* Upgrade Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="pp-card border-2 border-pp-navy"
              data-testid="upgrade-card"
            >
              {/* Title */}
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-pp-text text-center mb-3" data-testid="pricing-title">
                {t('premium.upgradeTitle')}
              </h1>
              
              {/* Description */}
              <p className="text-pp-text-secondary text-center mb-6 leading-relaxed">
                {t('premium.upgradeDescription')}
              </p>

              {/* Bullet points */}
              <div className="space-y-3 mb-8">
                {t('premium.upgradeFeatures').map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-pp-success-text flex-shrink-0 mt-0.5" weight="fill" />
                    <span className="text-pp-text">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Pricing Section - Value Anchoring */}
              <div className="text-center mb-6">
                {hasReferral ? (
                  /* WITH REFERRAL: $99 → $49 → $39 */
                  <>
                    <div className="flex items-baseline justify-center gap-3 mb-2">
                      {/* Original price */}
                      <span className="text-lg text-pp-text-muted line-through">
                        {language === 'fr' ? '99 $' : '$99'}
                      </span>
                      {/* Early access price - also crossed */}
                      <span className="text-lg text-pp-text-muted line-through">
                        {language === 'fr' ? '49 $' : '$49'}
                      </span>
                      {/* Final referral price */}
                      <span className="text-4xl sm:text-5xl font-heading font-bold text-pp-navy">
                        {language === 'fr' ? '39 $' : '$39'}
                      </span>
                    </div>
                    {/* Label with referral discount note */}
                    <p className="text-sm text-pp-success-text font-medium">
                      {pt.earlyAccessReferral}
                    </p>
                  </>
                ) : (
                  /* WITHOUT REFERRAL: $99 → $49 */
                  <>
                    <div className="flex items-baseline justify-center gap-3 mb-2">
                      {/* Original price */}
                      <span className="text-xl text-pp-text-muted line-through">
                        {language === 'fr' ? '99 $' : '$99'}
                      </span>
                      {/* Early access price */}
                      <span className="text-4xl sm:text-5xl font-heading font-bold text-pp-navy">
                        {language === 'fr' ? '49 $' : '$49'}
                      </span>
                    </div>
                    {/* Early access label */}
                    <p className="text-sm text-pp-success-text font-medium">
                      {pt.earlyAccess}
                    </p>
                  </>
                )}
                
                {/* One-time payment note */}
                <p className="text-sm text-pp-text-muted mt-2">
                  {pt.oneTime}
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 rounded-lg bg-pp-error-bg text-pp-error-text text-sm text-center">
                  {error}
                </div>
              )}

              {/* CTA Button */}
              <button 
                onClick={handleCheckout}
                disabled={loading}
                className="w-full h-12 bg-pp-navy hover:bg-pp-navy-dark text-white font-medium rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="pricing-cta"
              >
                {loading ? (
                  <>
                    <CircleNotch size={20} className="animate-spin" />
                    {language === 'fr' ? "Chargement..." : "Loading..."}
                  </>
                ) : (
                  <>
                    {t('premium.upgradeCta')}
                    <ArrowRight size={20} />
                  </>
                )}
              </button>

              {/* Supporting text */}
              <p className="text-xs text-pp-text-muted text-center mt-4 leading-relaxed">
                {t('premium.upgradeSupport')}
              </p>
            </motion.div>

            {/* FAQ Link */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-center mt-8"
            >
              <p className="text-pp-text-secondary mb-2">
                {t('faq.title').replace('?', '')}
              </p>
              <Link 
                to="/faq" 
                className="text-pp-navy font-medium hover:underline"
              >
                {t('faq.viewAll')}
              </Link>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
